/**
 * CoachDIA Discovery — Page principale de l'expérience conversationnelle.
 *
 * ⚠️ Marque user-facing : « COACHDIA · PRISM ». URL /discovery, data-testid `discovery-*`
 * et nom du fichier Discovery.jsx sont volontairement conservés pour préserver SEO,
 * QA automatisée et historique des données. Ne PAS renommer.
 *
 * Flow :
 *  1. Intro / CTA "Commencer"
 *  2. Bloc structuré : Likert 1-7 + forced-choice, une question à la fois
 *     (style conversationnel, animation entre questions)
 *  3. Bloc IA : 4 questions ouvertes adaptatives, saisie texte libre
 *  4. Finalisation -> teaser gratuit + CTA paiement 149€ (Stripe Checkout)
 *  5. Retour de Stripe -> affichage rapport complet + download PDF + email
 *
 * Soft paywall au login : géré via <DiscoveryPaywallModal /> dans App.js.
 * Hard paywall ici : tant que paid=false, on affiche teaser + CTA.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, ArrowRight, Download, Mail, Lock, CheckCircle2, Loader2, Share2 } from 'lucide-react';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import LikertScalePremium from '../components/discovery/LikertScalePremium';

const API = getApiUrl();

const ENERGY_COLORS = {
  red: { hex: '#C0392B', label: 'ROUGE · Action' },
  yellow: { hex: '#D4A017', label: 'JAUNE · Inspiration' },
  green: { hex: '#2D8659', label: 'VERT · Harmonie' },
  blue: { hex: '#1E4F8C', label: 'BLEU · Analyse' },
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─────────────────────────────────────────────────────────────
// Radar mini (affichage résultat)
// ─────────────────────────────────────────────────────────────
const RadarMini = ({ colors }) => {
  const cx = 150, cy = 150, r = 110;
  const axes = [
    { c: 'red', dx: 0, dy: -1 },
    { c: 'yellow', dx: 1, dy: 0 },
    { c: 'green', dx: 0, dy: 1 },
    { c: 'blue', dx: -1, dy: 0 },
  ];
  const pts = axes.map(({ c, dx, dy }) => {
    const v = (colors?.[c] || 0) / 100;
    return `${cx + dx * r * v},${cy + dy * r * v}`;
  }).join(' ');
  return (
    <svg viewBox="0 0 300 300" className="w-full max-w-xs mx-auto">
      {[25, 50, 75, 100].map(p => <circle key={p} cx={cx} cy={cy} r={r * p / 100} fill="none" stroke="#DDD6C7" strokeWidth="1" />)}
      {axes.map(({ dx, dy }, i) => <line key={i} x1={cx} y1={cy} x2={cx + dx * r} y2={cy + dy * r} stroke="#DDD6C7" strokeWidth="1" />)}
      <polygon points={pts} fill="#92710A" fillOpacity="0.25" stroke="#92710A" strokeWidth="2" />
      {axes.map(({ c, dx, dy }) => (
        <circle key={c} cx={cx + dx * r * (colors?.[c] || 0) / 100} cy={cy + dy * r * (colors?.[c] || 0) / 100} r="5" fill={ENERGY_COLORS[c].hex} />
      ))}
      <text x={cx} y={18} textAnchor="middle" fontSize="11" fill="#4B5563" fontFamily="Georgia">ROUGE</text>
      <text x={292} y={cy + 4} textAnchor="end" fontSize="11" fill="#4B5563" fontFamily="Georgia">JAUNE</text>
      <text x={cx} y={294} textAnchor="middle" fontSize="11" fill="#4B5563" fontFamily="Georgia">VERT</text>
      <text x={8} y={cy + 4} textAnchor="start" fontSize="11" fill="#4B5563" fontFamily="Georgia">BLEU</text>
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────
// Intro
// ─────────────────────────────────────────────────────────────
// ─── Écran de génération du rapport (chargement animé premium) ─────────
const FinalizingScreen = () => {
  const messages = [
    "Analyse de votre profil comportemental…",
    "Croisement des 29 questions et des 4 axes MBTI…",
    "Calcul de votre archétype et de vos angles morts…",
    "Génération des scénarios et recommandations signatures…",
    "Mise en forme du rapport premium…",
  ];
  const [idx, setIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const msgTimer = setInterval(() => {
      setIdx(i => (i + 1) % messages.length);
    }, 2200);
    const tickTimer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => { clearInterval(msgTimer); clearInterval(tickTimer); };
  }, []); // eslint-disable-line
  const pct = Math.min(92, elapsed * 9);
  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="discovery-finalizing-screen">
      <div className="max-w-md w-full px-6 text-center">
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full border-2 border-[#E5DFD0]"></div>
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#92710A] animate-spin"></div>
          <Sparkles className="absolute inset-0 m-auto w-8 h-8 text-[#92710A]" />
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-2">Génération en cours</div>
        <div className="font-serif text-xl text-[#0F0F10] mb-4 min-h-[56px]" key={idx}>
          {messages[idx]}
        </div>
        <div className="w-full h-[3px] bg-[#E5DFD0] rounded-full overflow-hidden mb-2">
          <div className="h-full bg-[#92710A] transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-xs text-[#4B5563] italic">
          Environ 10 secondes — n'actualisez pas la page
        </div>
      </div>
    </div>
  );
};


const Intro = ({ onStart, starting }) => (
  <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="discovery-intro">
    <div className="inline-block mb-6 text-[11px] tracking-[0.3em] text-[#92710A] uppercase font-semibold">
      COACHDIA · PRISM
    </div>
    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0F0F10] leading-tight tracking-tight">
      Révélez votre profil<br />comportemental & professionnel
    </h1>
    <div className="w-16 h-[2px] bg-[#92710A] mx-auto my-8" />
    <p className="text-[#4B5563] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
      Une expérience de profilage premium, sobre et rigoureuse. Nous croisons une méthodologie
      jungienne avec une IA conversationnelle pour vous livrer un rapport personnalisé profond :
      vos forces, vos angles morts, et un plan de développement concret sur 90 jours.
    </p>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
      {Object.entries(ENERGY_COLORS).map(([k, v]) => (
        <div key={k} className="border border-[#E5DFD0] rounded-sm p-4">
          <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ background: v.hex }} />
          <div className="text-xs tracking-widest text-[#4B5563]">{v.label}</div>
        </div>
      ))}
    </div>
    <div className="mt-12 flex flex-col items-center gap-4">
      <Button
        data-testid="discovery-start-btn"
        onClick={onStart}
        disabled={starting}
        className="bg-[#0F0F10] hover:bg-[#1C1C1E] text-white rounded-full px-10 py-6 text-base tracking-wide"
      >
        {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
        Commencer l'expérience
      </Button>
      <div className="text-xs text-[#4B5563]" data-testid="discovery-meta-label">
        12 à 15 minutes · Profil personnalisé
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Question structurée — Iter389 (refonte premium executive)
// Carte enveloppe ivoire/or, progression en chip + barre épaisse,
// question serif respirante, scale 1-7 segmentée premium, ancres
// simplifiées en 2 colonnes, Précédent intégré au footer.
// ─────────────────────────────────────────────────────────────
const StructuredQuestion = ({ question, progress, onAnswer, onBack, canGoBack, saving }) => {
  const isForcedChoice = Array.isArray(question.options);
  const handleClick = (v, e) => {
    // Retire le focus pour éviter que :hover reste collé sur la question suivante
    try { e.currentTarget.blur(); } catch (_) { /* ignore */ }
    onAnswer(v);
  };
  const pct = Math.round(progress?.percent || 0);
  const qNum = (progress?.structured_done || 0) + 1;
  const qTotal = progress?.structured_total || 1;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 lg:py-16" data-testid={`discovery-question-${question.id}`}>
      {/* Carte enveloppe premium */}
      <div
        className="relative bg-white rounded-3xl border overflow-hidden"
        style={{
          borderColor: '#EBE4D2',
          boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 30px 60px -30px rgba(28,17,71,0.18), 0 8px 24px -12px rgba(146,113,10,0.08)',
        }}
      >
        {/* Halo or subtil en arrière-plan */}
        <div
          aria-hidden
          className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0) 70%)',
            filter: 'blur(40px)',
          }}
        />

        <div className="relative p-6 sm:p-9 lg:p-12">
          {/* Header : chip Question + pourcent serif premium */}
          <div className="flex items-center justify-between mb-4 sm:mb-5 gap-3">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold"
              style={{
                background: 'linear-gradient(135deg, #FFFCEC 0%, #FBF9F3 100%)',
                color: '#92710A',
                border: '1px solid #EBE4D2',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
              }}
              data-testid="discovery-progress-chip"
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#D4AF37' }} />
              Question {qNum} / {qTotal}
            </div>
            <div className="flex items-baseline gap-1 shrink-0">
              <span
                className="font-serif tabular-nums leading-none"
                style={{
                  fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
                  fontSize: 'clamp(22px, 4.2vw, 32px)',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #1C1147 0%, #92710A 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {pct}
              </span>
              <span className="text-[12px] font-semibold" style={{ color: '#92710A' }}>%</span>
            </div>
          </div>

          {/* Barre de progression épaisse premium */}
          <div
            className="relative w-full rounded-full overflow-hidden mb-7 sm:mb-9"
            style={{ height: 6, background: '#F0EAD8' }}
            aria-hidden
          >
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${pct}%`,
                background: 'linear-gradient(90deg, #92710A 0%, #D4AF37 60%, #F3D060 100%)',
                boxShadow: '0 0 12px rgba(212,175,55,0.5)',
              }}
            />
            {pct > 0 && pct < 100 && (
              <span
                aria-hidden
                className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full"
                style={{
                  left: `calc(${pct}% - 5px)`,
                  background: '#FFFFFF',
                  border: '2px solid #D4AF37',
                  boxShadow: '0 0 0 3px rgba(212,175,55,0.25)',
                }}
              />
            )}
          </div>

          {/* Question premium */}
          <h2
            className="font-serif leading-[1.2] mb-8 sm:mb-10"
            style={{
              fontFamily: '"Playfair Display", "Cormorant Garamond", Georgia, serif',
              fontSize: 'clamp(22px, 5vw, 34px)',
              color: '#1C1147',
              fontWeight: 500,
              letterSpacing: '-0.01em',
            }}
            data-testid="discovery-question-text"
          >
            {question.text}
          </h2>

          {/* Échelle / choix forcés */}
          {isForcedChoice ? (
            <div className="space-y-3">
              {question.options.map(opt => (
                <button
                  key={opt.id}
                  data-testid={`discovery-option-${question.id}-${opt.id}`}
                  onClick={(e) => handleClick(opt.id, e)}
                  disabled={saving}
                  className="w-full text-left border focus:outline-none focus:border-[#92710A] hover:border-[#92710A] hover:bg-[#FBF8F1] rounded-2xl px-5 py-4 transition-all group disabled:opacity-60"
                  style={{ borderColor: '#EBE4D2' }}
                >
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full border text-[12px] font-bold mr-3 transition-colors"
                    style={{
                      borderColor: '#D4AF37',
                      color: '#92710A',
                      background: '#FFFCEC',
                    }}
                  >
                    {opt.id.toUpperCase()}
                  </span>
                  <span style={{ color: '#1C1147' }}>{opt.label}</span>
                </button>
              ))}
            </div>
          ) : (
            <LikertScalePremium
              value={null}
              disabled={saving}
              questionId={question.id}
              onSelect={(v) => onAnswer(v)}
            />
          )}

          {/* Footer : Précédent intégré (plus orphelin) */}
          <div
            className="mt-8 sm:mt-10 pt-5 sm:pt-6 flex items-center justify-between gap-3"
            style={{ borderTop: '1px solid #F0EAD8' }}
          >
            <button
              data-testid="discovery-back-btn"
              onClick={onBack}
              disabled={saving || !canGoBack}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] tracking-[0.22em] uppercase font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#FBF8F1]"
              style={{ color: '#4B5563' }}
            >
              <span>←</span> Précédent
            </button>
            <span
              className="hidden sm:block text-[10.5px] uppercase tracking-[0.18em] italic"
              style={{ color: '#8A7A5E', opacity: 0.7 }}
            >
              Cliquez pour répondre · réponse enregistrée automatiquement
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Bloc IA ouvertes
// ─────────────────────────────────────────────────────────────
const OpenQuestion = ({ question, index, total, onSubmit, onBack, canGoBack, saving }) => {
  // Key-based remount assure un state vierge à chaque question (pas de pré-remplissage)
  const [text, setText] = useState('');
  return (
    <div className="max-w-2xl mx-auto px-6 py-10" data-testid={`discovery-ai-question-${question.id}`}>
      <div className="text-xs tracking-widest text-[#92710A] uppercase mb-4">
        Approfondissement {index + 1} / {total}
      </div>
      <h2 className="font-serif text-2xl text-[#0F0F10] leading-snug mb-6">
        {question.text}
      </h2>
      <textarea
        data-testid={`discovery-ai-input-${question.id}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        autoFocus
        placeholder="Partagez une situation vécue, un exemple concret…"
        className="w-full border border-[#E5DFD0] rounded-sm p-4 text-[#0F0F10] focus:border-[#92710A] focus:outline-none font-sans text-[15px] leading-relaxed"
      />
      <div className="flex justify-between items-center mt-3">
        <span className="text-xs text-[#4B5563]">{text.length} / 2000 caractères · minimum 5</span>
        <Button
          data-testid={`discovery-ai-submit-${question.id}`}
          onClick={() => onSubmit(text)}
          disabled={saving || text.trim().length < 5}
          className="bg-[#0F0F10] hover:bg-[#1C1C1E] text-white rounded-full px-8"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
          {index + 1 === total ? 'Finaliser mon profil' : 'Suivant'}
        </Button>
      </div>
      {/* Précédent — revenir à la question IA précédente (client-side, pas de backend) */}
      <div className="mt-6 flex justify-start">
        <button
          data-testid="discovery-ai-back-btn"
          onClick={onBack}
          disabled={saving || !canGoBack}
          className="text-xs tracking-widest uppercase text-[#4B5563] hover:text-[#0F0F10] disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          ← Précédent
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Teaser (after finalize, before payment) — effet miroir irrésistible
// ─────────────────────────────────────────────────────────────
const Teaser = ({ summary, pricing, onPay, paying }) => {
  const archetype = summary?.archetype_name || '';
  const tag = summary?.archetype_tagline || '';
  const desc = summary?.archetype_description || '';
  const colors = summary?.colors || {};
  const dom = summary?.dominant_color;
  const sec = summary?.secondary_color;
  const mirror = summary?.mirror_insight || {};
  const unlock = summary?.unlock_metrics || {};
  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="discovery-teaser">
      <div className="text-center mb-8">
        <div className="inline-block mb-3 text-[11px] tracking-[0.3em] text-[#92710A] uppercase">Votre profil est prêt</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#0F0F10] leading-tight">{archetype}</h1>
        {tag && <div className="text-[#4B5563] italic mt-3 text-lg">« {tag} »</div>}
      </div>

      {/* Radar partiellement flouté */}
      <div className="relative max-w-sm mx-auto">
        <RadarMini colors={colors} />
        <div
          className="absolute inset-0 pointer-events-none flex items-center justify-center"
          style={{
            background: 'radial-gradient(circle at center, transparent 40%, rgba(251,248,241,0.92) 72%)',
            backdropFilter: 'blur(1.5px)',
          }}
        >
          <div className="text-center">
            <Lock className="w-5 h-5 text-[#92710A] mx-auto mb-2" />
            <div className="text-[10px] tracking-[0.3em] text-[#92710A] uppercase">Graphique complet<br />verrouillé</div>
          </div>
        </div>
      </div>

      {/* MIRROR INSIGHT — pièce maîtresse (effet "il me comprend") */}
      {(mirror.strength_title || mirror.blind_title) && (
        <div className="max-w-xl mx-auto mt-8 space-y-3" data-testid="discovery-teaser-mirror">
          {mirror.strength_title && (
            <div className="bg-white border-l-4 border-[#92710A] p-5 rounded-sm" data-testid="discovery-teaser-insight">
              <div className="text-[10px] tracking-[0.3em] uppercase mb-2 text-[#92710A]">Ce que nous avons vu chez vous</div>
              <div className="font-serif text-lg text-[#0F0F10] mb-2">{mirror.strength_title}</div>
              <p className="text-sm text-[#4B5563] leading-relaxed italic">
                {mirror.strength_preview}{mirror.strength_preview && mirror.strength_preview.length >= 175 ? '…' : ''}
              </p>
            </div>
          )}
          {mirror.blind_title && (
            <div className="bg-[#FBF8F1] border border-[#E5DFD0] p-5 rounded-sm">
              <div className="text-[10px] tracking-[0.3em] uppercase mb-2 text-[#C0392B]">Un angle mort à ne pas ignorer</div>
              <div className="font-serif text-lg text-[#0F0F10]">{mirror.blind_title}</div>
              {mirror.blind_trigger && (
                <p className="text-xs text-[#4B5563] italic mt-1">Déclencheur : {mirror.blind_trigger}</p>
              )}
              <p className="text-xs text-[#92710A] mt-2 font-semibold">
                Le détail et les leviers concrets sont dans le rapport complet.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Signal "presque complet" — sensation de frustration positive */}
      {(unlock.strengths_count > 0 || unlock.blind_spots_count > 0) && (
        <div className="max-w-xl mx-auto mt-6 bg-[#0F0F10] text-white p-5 rounded-sm" data-testid="discovery-teaser-almost">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-3">Ce qui vous attend</div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="font-serif text-2xl text-[#92710A]">{unlock.strengths_count}</div>
              <div className="text-[10px] tracking-widest text-[#C4B27A] uppercase mt-1">Forces identifiées</div>
            </div>
            <div>
              <div className="font-serif text-2xl text-[#92710A]">{unlock.blind_spots_count}</div>
              <div className="text-[10px] tracking-widest text-[#C4B27A] uppercase mt-1">Angles morts</div>
            </div>
            <div>
              <div className="font-serif text-2xl text-[#92710A]">{unlock.development_axes_count}</div>
              <div className="text-[10px] tracking-widest text-[#C4B27A] uppercase mt-1">Axes 30/60/90</div>
            </div>
          </div>
          <div className="text-center text-[11px] text-[#C4B27A] italic mt-4">
            Tout est déjà rédigé. Plus qu'une étape pour y accéder.
          </div>
        </div>
      )}

      {desc && (
        <p className="max-w-xl mx-auto text-center text-sm text-[#4B5563] italic mt-6 px-4">
          {desc.substring(0, 200)}{desc.length > 200 ? '…' : ''}
        </p>
      )}

      {/* CTA bloc noir — bénéfices concrets + preuve de profondeur */}
      <div className="bg-[#0F0F10] text-white mt-8 p-8 rounded-sm" data-testid="discovery-teaser-cta-block">
        <div className="text-center mb-6">
          <div className="text-[11px] tracking-[0.3em] text-[#92710A] uppercase mb-2">Votre rapport</div>
          <h3 className="font-serif text-2xl">Déverrouillez ce qui vous attend</h3>
        </div>

        {/* Preuve de profondeur — 3 signaux forts */}
        <div className="grid sm:grid-cols-3 gap-3 max-w-xl mx-auto mb-7" data-testid="discovery-teaser-proof">
          <div className="text-center border border-[#92710A]/30 rounded-sm p-3">
            <CheckCircle2 className="w-4 h-4 text-[#92710A] mx-auto mb-1" />
            <div className="text-[10px] tracking-wider text-[#C4B27A] uppercase leading-tight">Rapport<br />déjà généré</div>
          </div>
          <div className="text-center border border-[#92710A]/30 rounded-sm p-3">
            <CheckCircle2 className="w-4 h-4 text-[#92710A] mx-auto mb-1" />
            <div className="text-[10px] tracking-wider text-[#C4B27A] uppercase leading-tight">Analyse<br />déjà prête</div>
          </div>
          <div className="text-center border border-[#92710A]/30 rounded-sm p-3">
            <CheckCircle2 className="w-4 h-4 text-[#92710A] mx-auto mb-1" />
            <div className="text-[10px] tracking-wider text-[#C4B27A] uppercase leading-tight">Profil structuré<br />activable</div>
          </div>
        </div>

        {/* 5 bénéfices concrets */}
        <div className="max-w-lg mx-auto" data-testid="discovery-teaser-benefits">
          <div className="grid gap-2.5">
            <div className="flex gap-3 items-start">
              <span className="text-[#92710A] font-serif text-lg leading-none mt-0.5">—</span>
              <div className="text-sm text-[#E5DFD0]">
                <b className="text-white">Carrière</b> · identifier vos leviers d'impact et les zones qui freinent votre progression.
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <span className="text-[#92710A] font-serif text-lg leading-none mt-0.5">—</span>
              <div className="text-sm text-[#E5DFD0]">
                <b className="text-white">Leadership</b> · comprendre votre style de décision et le calibrer selon les contextes.
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <span className="text-[#92710A] font-serif text-lg leading-none mt-0.5">—</span>
              <div className="text-sm text-[#E5DFD0]">
                <b className="text-white">Communication</b> · ce que vous faites bien, et ce qui vous coûte sans que vous le sachiez.
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <span className="text-[#92710A] font-serif text-lg leading-none mt-0.5">—</span>
              <div className="text-sm text-[#E5DFD0]">
                <b className="text-white">Prise de recul</b> · reconnaître vos signaux d'alerte et vos leviers sous pression.
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <span className="text-[#92710A] font-serif text-lg leading-none mt-0.5">—</span>
              <div className="text-sm text-[#E5DFD0]">
                <b className="text-white">Axes de progression</b> · un plan 30/60/90 jours taillé pour vous.
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center gap-3">
          <Button
            data-testid="discovery-pay-btn"
            onClick={onPay}
            disabled={paying}
            className="bg-[#92710A] hover:bg-[#A88415] text-white rounded-full px-10 py-6 text-base tracking-wide font-serif mx-auto"
          >
            {paying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Débloquer mon rapport
            {pricing?.amount_eur != null && (
              <span className="ml-3 opacity-90">
                {pricing.promo_active && pricing.base_price_eur > pricing.amount_eur ? (
                  <> · <s className="opacity-60">{pricing.base_price_eur.toFixed(0)} €</s> {pricing.amount_eur.toFixed(0)} €</>
                ) : (
                  <> · {pricing.amount_eur.toFixed(0)} €</>
                )}
              </span>
            )}
          </Button>
          {pricing?.promo_active && pricing?.promo_label && (
            <div className="text-[11px] text-[#C4B27A] tracking-wide text-center" data-testid="discovery-teaser-promo">
              {pricing.promo_label} · -{pricing.discount_pct?.toFixed(0)}%
              {pricing.promo_ends_at ? ` · valide jusqu'au ${new Date(pricing.promo_ends_at).toLocaleDateString('fr-FR')}` : ''}
            </div>
          )}
          <div className="text-[11px] text-[#8E8878] text-center">
            Paiement sécurisé Stripe · Accès immédiat · PDF téléchargeable
          </div>
          {/* Sortie non bloquante — l'utilisateur peut revenir payer plus tard */}
          <button
            type="button"
            data-testid="discovery-pay-later-btn"
            onClick={() => { window.location.href = '/my-discovery'; }}
            className="mt-2 text-[11px] tracking-widest uppercase text-[#8E8878] hover:text-[#C4B27A] transition-colors"
          >
            Plus tard · retour à l'accueil
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Full report (after payment)
// ─────────────────────────────────────────────────────────────
const FullReport = ({ summary }) => {
  const navigate = useNavigate();
  const [emailSending, setEmailSending] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sharePreview, setSharePreview] = useState(null);
  const [shareImageUrl, setShareImageUrl] = useState(null);
  const [shareShortUrl, setShareShortUrl] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const full = summary?.full || {};
  const narr = full.narrative || {};
  const arche = full.archetype || {};
  const scores = full.scores || {};
  const colors = summary?.colors || scores.colors || {};
  const mbti = scores.mbti || summary?.mbti || {};
  const mbtiCode = scores.mbti_code || summary?.mbti_code || '';
  // Iter333 — top 3 et quick actions (alignés sur PDF Niveau I)
  const top3Strengths = (narr.strengths || []).slice(0, 3);
  const top3BlindSpots = (narr.blind_spots || []).slice(0, 3);
  const quickActions = (() => {
    const axes = narr.development_axes || [];
    const out = [];
    for (const a of axes.slice(0, 3)) {
      const act = a.action_30d || a.title;
      if (act) out.push(act);
    }
    while (out.length < 3) out.push('Identifier un pair fiable comme sounding-board hebdomadaire.');
    return out;
  })();
  const comm = narr.communication_style || {};
  const stress = narr.stress_profile || {};
  const team = narr.team_fit || {};

  const download = async () => {
    setDownloadLoading(true);
    try {
      const res = await axios.get(`${API}/discovery/report/pdf`, { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'coachdia-prism.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Téléchargement impossible');
    } finally {
      setDownloadLoading(false);
    }
  };

  const openShareModal = async () => {
    setShareModalOpen(true);
    if (sharePreview && shareImageUrl) return;
    setShareLoading(true);
    try {
      const [prev, img, short] = await Promise.all([
        axios.get(`${API}/discovery/share-card/preview`, { headers: authHeaders() }),
        axios.get(`${API}/discovery/share-card`, { headers: authHeaders(), responseType: 'blob' }),
        axios.get(`${API}/discovery/share-card/short-link`, { headers: authHeaders() }),
      ]);
      setSharePreview(prev.data);
      setShareImageUrl(URL.createObjectURL(img.data));
      setShareShortUrl(`${window.location.origin}/api${short.data.short_path}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Carte indisponible');
      setShareModalOpen(false);
    } finally {
      setShareLoading(false);
    }
  };

  const copyShortUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareShortUrl);
      toast.success('Lien copié — collez-le dans votre post LinkedIn');
    } catch {
      toast.error('Copie impossible, sélectionnez et copiez manuellement');
    }
  };

  const downloadShareCard = () => {
    if (!shareImageUrl) return;
    const a = document.createElement('a');
    a.href = shareImageUrl;
    a.download = 'coachdia-prism-card.png';
    a.click();
    toast.success('Carte téléchargée — prête pour LinkedIn');
  };

  const sendEmail = async () => {
    setEmailSending(true);
    try {
      const r = await axios.post(`${API}/discovery/report/email`, {}, { headers: authHeaders() });
      if (r.data?.sent) toast.success(`Envoyé à ${r.data.recipient}`);
      else toast.error('Email non envoyé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi email');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="discovery-full-report">
      <div className="mb-6">
        <BackButton />
      </div>
      <div className="text-center mb-10">
        <CheckCircle2 className="w-8 h-8 text-[#2D8659] mx-auto mb-3" />
        <div className="text-[11px] tracking-[0.3em] text-[#92710A] uppercase">Votre rapport est activé</div>
        <h1 className="font-serif text-4xl text-[#0F0F10] mt-2">{arche.name}</h1>
        <p className="text-[#4B5563] italic mt-2">« {arche.tagline} »</p>
      </div>

      <div className="flex flex-wrap justify-center gap-3 mb-12">
        <Button data-testid="discovery-download-pdf-btn" onClick={download} disabled={downloadLoading} className="bg-[#0F0F10] hover:bg-[#1C1C1E] text-white rounded-full">
          {downloadLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Télécharger le PDF
        </Button>
        <Button data-testid="discovery-email-btn" onClick={sendEmail} disabled={emailSending} variant="outline" className="rounded-full border-[#C4B27A] text-[#0F0F10]">
          {emailSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
          M'envoyer par email
        </Button>
        <Button data-testid="discovery-see-coaches-btn" onClick={() => navigate('/discovery/coaches')} variant="outline" className="rounded-full border-[#C4B27A] text-[#0F0F10]">
          Voir mes coachs recommandés
        </Button>
        <Button data-testid="discovery-share-linkedin-btn" onClick={openShareModal} variant="outline" className="rounded-full border-[#92710A] text-[#92710A] hover:bg-[#FBF8F1]">
          <Share2 className="w-4 h-4 mr-2" /> Partager mon profil
        </Button>
      </div>

      {/* Share card modal — premium, accessible uniquement aux profils payés */}
      {shareModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-card-modal" onClick={() => setShareModalOpen(false)}>
          <div className="bg-white max-w-lg w-full p-6 rounded-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] mb-2">Partage premium · Réservé aux profils payés</div>
            <h3 className="font-serif text-2xl text-[#0F0F10] mb-4">Votre carte LinkedIn</h3>
            {shareLoading ? (
              <div className="flex items-center gap-2 text-[#4B5563] py-16 justify-center">
                <Loader2 className="w-5 h-5 animate-spin" /> Génération de la carte...
              </div>
            ) : shareImageUrl ? (
              <>
                <img src={shareImageUrl} alt="Aperçu carte LinkedIn" className="w-full aspect-square object-contain border border-stone-200 rounded-sm" data-testid="share-card-preview-image" />
                {shareShortUrl && (
                  <div className="mt-3 p-3 bg-[#FBF9F5] border border-[#C4B27A]/40 rounded-sm" data-testid="share-card-short-link-block">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#92710A] font-semibold mb-1">Lien de partage trackable</div>
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={shareShortUrl}
                        data-testid="share-card-short-link-input"
                        className="flex-1 text-xs px-2 py-1 bg-white border border-stone-200 rounded-sm font-mono"
                        onFocus={(e) => e.target.select()}
                      />
                      <Button size="sm" variant="outline" onClick={copyShortUrl} data-testid="share-card-copy-short-link"
                              className="border-[#92710A] text-[#92710A] hover:bg-white rounded-sm">
                        Copier
                      </Button>
                    </div>
                    <p className="text-[10px] text-stone-500 mt-1.5 italic">Chaque clic est compté pour mesurer l'impact LinkedIn.</p>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2 justify-between items-center">
                  <div className="text-xs text-[#4B5563]">
                    <div><b>{sharePreview?.archetype_name}</b> · {sharePreview?.mbti_code}</div>
                    <div className="italic text-stone-500">Format 1200×1200 · optimisé LinkedIn</div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShareModalOpen(false)} className="rounded-sm" data-testid="share-card-close-btn">Fermer</Button>
                    <Button onClick={downloadShareCard} data-testid="share-card-download-btn" className="bg-[#92710A] hover:bg-[#7a5d08] text-white rounded-sm">
                      <Download className="w-4 h-4 mr-2" /> Télécharger
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      <RadarMini colors={summary?.colors || {}} />

      {/* ════════ NIVEAU I — SYNTHÈSE IMMÉDIATE (mirroir PDF) ════════ */}
      <div className="mt-12 mb-2 text-center" data-testid="discovery-level-i">
        <div className="text-[10px] tracking-[0.3em] text-[#92710A] uppercase">Niveau I</div>
        <h2 className="font-serif text-3xl text-[#0F0F10] mt-1">Synthèse immédiate</h2>
        <p className="text-sm text-[#4B5563] italic mt-1">Votre profil en cinq minutes.</p>
      </div>

      {arche.description && (
        <section className="mt-8" data-testid="discovery-section-archetype">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Votre archétype</h2>
          <div className="border border-[#92710A]/40 bg-[#FBF8F1] rounded-sm p-5 text-center">
            <div className="font-serif text-2xl text-[#92710A]">{arche.name}</div>
            <div className="text-sm italic text-[#4B5563] mt-1">« {arche.tagline} »</div>
          </div>
          <p className="text-[#0F0F10] leading-relaxed mt-4">{arche.description}</p>
        </section>
      )}

      {Object.keys(colors).length > 0 && (
        <section className="mt-10" data-testid="discovery-section-energies-kpi">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Votre cartographie d'énergies</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { k: 'red', label: 'Rouge', hex: '#C0392B' },
              { k: 'yellow', label: 'Jaune', hex: '#D4A017' },
              { k: 'green', label: 'Vert', hex: '#2D8659' },
              { k: 'blue', label: 'Bleu', hex: '#1E4F8C' },
            ].map(({ k, label, hex }) => (
              <div key={k} className="border border-[#E5DFD0] bg-white rounded-sm p-4 text-center">
                <div className="font-serif text-3xl" style={{ color: hex }}>{Math.round(colors[k] || 0)}</div>
                <div className="text-[10px] tracking-widest uppercase text-[#4B5563] mt-1">{label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {top3Strengths.length > 0 && (
        <section className="mt-10" data-testid="discovery-section-top3-strengths">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Vos 3 forces clés</h2>
          <ol className="list-decimal list-inside space-y-2 text-[#0F0F10]">
            {top3Strengths.map((s, i) => (
              <li key={i}><b>{s.title}</b> — {s.detail}</li>
            ))}
          </ol>
        </section>
      )}

      {top3BlindSpots.length > 0 && (
        <section className="mt-10" data-testid="discovery-section-top3-blind">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Vos 3 angles morts</h2>
          <ol className="list-decimal list-inside space-y-2 text-[#0F0F10]">
            {top3BlindSpots.map((b, i) => (
              <li key={i}><b>{b.title}</b> — {b.detail}</li>
            ))}
          </ol>
        </section>
      )}

      <section className="mt-10" data-testid="discovery-section-quick-actions">
        <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">3 actions à lancer cette semaine</h2>
        <ol className="list-decimal list-inside space-y-2 text-[#0F0F10] border border-[#92710A]/30 bg-[#FBF8F1] rounded-sm p-5">
          {quickActions.map((a, i) => <li key={i}>{a}</li>)}
        </ol>
      </section>

      {/* ════════ NIVEAU II — RAPPORT PRINCIPAL (mirroir PDF) ════════ */}
      <div className="mt-16 mb-2 text-center" data-testid="discovery-level-ii">
        <div className="text-[10px] tracking-[0.3em] text-[#92710A] uppercase">Niveau II</div>
        <h2 className="font-serif text-3xl text-[#0F0F10] mt-1">Rapport principal</h2>
        <p className="text-sm text-[#4B5563] italic mt-1">Une analyse complète : comment vous décidez, communiquez, pilotez.</p>
      </div>

      <section className="mt-8">
        <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Synthèse exécutive</h2>
        <p className="text-[#0F0F10] leading-relaxed">{narr.executive_summary}</p>
      </section>

      {/* Grille de lecture des 4 énergies (parité PDF) */}
      <section className="mt-10" data-testid="discovery-section-energy-grid">
        <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Grille de lecture — les 4 énergies</h2>
        <p className="text-sm text-[#4B5563] italic mb-4">Aucune n'est meilleure qu'une autre — c'est l'équilibre qui fait votre signature.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { hex: '#C0392B', name: 'ROUGE · Action', sense: 'Énergie de décision, de direction, de résultat.', scale: '> 70 : moteur, tranche vite, assume · 40-70 : engage quand il faut · < 40 : préfère la délibération à la décision.' },
            { hex: '#D4A017', name: 'JAUNE · Inspiration', sense: 'Énergie de vision, d\'embarquement, de créativité.', scale: '> 70 : charismatique, ouvre des horizons · 40-70 : mobilise autour d\'une cause · < 40 : préfère l\'exécution à l\'évangélisation.' },
            { hex: '#2D8659', name: 'VERT · Harmonie', sense: 'Énergie de lien, de soin du collectif, de stabilité.', scale: '> 70 : fédère, protège le groupe · 40-70 : attentif aux dynamiques · < 40 : priorise le résultat sur le lien.' },
            { hex: '#1E4F8C', name: 'BLEU · Analyse', sense: 'Énergie de rigueur, de structure, de vérité factuelle.', scale: '> 70 : architecte, exige la preuve · 40-70 : apporte de la structure · < 40 : préfère l\'intuition à la donnée.' },
          ].map((e, i) => (
            <div key={i} className="border border-[#E5DFD0] bg-white rounded-sm p-4">
              <div className="font-semibold mb-1" style={{ color: e.hex }}>{e.name}</div>
              <p className="text-sm text-[#0F0F10] mb-2">{e.sense}</p>
              <p className="text-xs text-[#4B5563]">{e.scale}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Vos cinq forces</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {(narr.strengths || []).map((s, i) => (
            <Card key={i} className="border-l-4 border-[#92710A] bg-[#FBF8F1] rounded-sm p-4">
              <h4 className="font-serif text-[#92710A] mb-1">{s.title}</h4>
              <p className="text-sm text-[#0F0F10] leading-relaxed">{s.detail}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Vos angles morts</h2>
        <p className="text-sm text-[#4B5563] italic mb-4">Ce ne sont pas des défauts — ce sont les revers de vos qualités.</p>
        <div className="space-y-3">
          {(narr.blind_spots || []).map((b, i) => (
            <Card key={i} className="border-l-4 border-[#C0392B] bg-white rounded-sm p-4">
              <h4 className="font-serif text-[#C0392B] mb-1">{b.title}</h4>
              {b.trigger && <p className="text-xs text-[#4B5563] italic mb-1">Déclencheur : {b.trigger}</p>}
              <p className="text-sm text-[#0F0F10] leading-relaxed">{b.detail}</p>
              {b.situation && (
                <p className="text-sm text-[#0F0F10] leading-relaxed mt-2"><span className="text-xs uppercase tracking-widest text-[#4B5563] mr-2">Situation</span>{b.situation}</p>
              )}
              {b.observable_behavior && (
                <p className="text-sm text-[#0F0F10] leading-relaxed mt-1"><span className="text-xs uppercase tracking-widest text-[#4B5563] mr-2">Ce qu'on voit</span>{b.observable_behavior}</p>
              )}
              {b.impact && (
                <p className="text-sm text-[#0F0F10] leading-relaxed mt-1"><span className="text-xs uppercase tracking-widest text-[#92710A] mr-2">Impact réel</span>{b.impact}</p>
              )}
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Plan 30 / 60 / 90 jours</h2>
        <div className="space-y-4">
          {(narr.development_axes || []).map((a, i) => (
            <div key={i} className="border border-[#E5DFD0] rounded-sm p-5">
              <h3 className="font-serif text-lg text-[#92710A]">{a.title}</h3>
              <p className="text-sm text-[#4B5563] italic mb-3">{a.why}</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {['30d', '60d', '90d'].map(k => (
                  <div key={k} className="border border-[#E5DFD0] p-3 rounded-sm bg-[#FBF8F1]">
                    <div className="text-[10px] tracking-widest text-[#92710A] font-semibold uppercase mb-1">{k.replace('d', ' jours')}</div>
                    <p className="text-sm">{a[`action_${k}`]}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* === Iter332 — Blocs premium précédemment masqués (5 → 16) === */}

      {(comm.summary || (Array.isArray(comm.do) && comm.do.length) || (Array.isArray(comm.avoid) && comm.avoid.length)) && (
        <section className="mt-10" data-testid="discovery-section-communication">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Style de communication</h2>
          {comm.summary && <p className="text-[#0F0F10] leading-relaxed mb-3">{comm.summary}</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            {Array.isArray(comm.do) && comm.do.length > 0 && (
              <div className="border border-[#E5DFD0] bg-[#FBF8F1] rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#2D8659] font-semibold uppercase mb-2">À faire</div>
                <ul className="text-sm text-[#0F0F10] space-y-1 list-disc list-inside">
                  {comm.do.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(comm.avoid) && comm.avoid.length > 0 && (
              <div className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#C0392B] font-semibold uppercase mb-2">À éviter</div>
                <ul className="text-sm text-[#0F0F10] space-y-1 list-disc list-inside">
                  {comm.avoid.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {narr.decision_style && (
        <section className="mt-10" data-testid="discovery-section-decision">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Style de décision</h2>
          <p className="text-[#0F0F10] leading-relaxed">{narr.decision_style}</p>
        </section>
      )}

      {(team.best_with || team.friction_with) && (
        <section className="mt-10" data-testid="discovery-section-team">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Interactions professionnelles</h2>
          {team.best_with && (
            <div className="mb-3">
              <h3 className="font-serif text-lg text-[#2D8659] mb-1">Vos meilleurs alliés</h3>
              <p className="text-[#0F0F10] leading-relaxed">{team.best_with}</p>
            </div>
          )}
          {team.friction_with && (
            <div>
              <h3 className="font-serif text-lg text-[#C0392B] mb-1">Frictions récurrentes</h3>
              <p className="text-[#0F0F10] leading-relaxed">{team.friction_with}</p>
            </div>
          )}
        </section>
      )}

      {(stress.reaction || (Array.isArray(stress.signals) && stress.signals.length) || (Array.isArray(stress.levers) && stress.levers.length)) && (
        <section className="mt-10" data-testid="discovery-section-stress-simple">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Profil sous pression</h2>
          {stress.reaction && <p className="text-[#0F0F10] leading-relaxed mb-3"><b>Votre réaction :</b> {stress.reaction}</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            {Array.isArray(stress.signals) && stress.signals.length > 0 && (
              <div className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#C0392B] font-semibold uppercase mb-2">Signaux d'alerte</div>
                <ul className="text-sm text-[#0F0F10] space-y-1 list-disc list-inside">
                  {stress.signals.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(stress.levers) && stress.levers.length > 0 && (
              <div className="border border-[#E5DFD0] bg-[#FBF8F1] rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#2D8659] font-semibold uppercase mb-2">Vos leviers</div>
                <ul className="text-sm text-[#0F0F10] space-y-1 list-disc list-inside">
                  {stress.levers.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {narr.coach_recommendation_brief && (
        <section className="mt-10" data-testid="discovery-section-coach-brief">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Ce qu'un accompagnement humain peut vous apporter</h2>
          <p className="text-[#0F0F10] leading-relaxed">{narr.coach_recommendation_brief}</p>
          <Card className="border-l-4 border-[#92710A] bg-[#FBF8F1] rounded-sm p-4 mt-4">
            <h4 className="font-serif text-[#92710A] mb-1">Prochaine étape</h4>
            <p className="text-sm text-[#0F0F10]">Depuis votre tableau de bord CoachDIA, retrouvez la sélection de coachs humains spécialement recommandés pour votre profil — triés sur la base de votre cartographie et de leurs spécialités.</p>
          </Card>
        </section>
      )}

      {/* ════════ NIVEAU III — ANNEXES (mirroir PDF) ════════ */}
      <div className="mt-16 mb-2 text-center" data-testid="discovery-level-iii">
        <div className="text-[10px] tracking-[0.3em] text-[#92710A] uppercase">Niveau III</div>
        <h2 className="font-serif text-3xl text-[#0F0F10] mt-1">Annexes</h2>
        <p className="text-sm text-[#4B5563] italic mt-1">Approfondissements pour aller plus loin.</p>
      </div>

      {Array.isArray(narr.scenarios) && narr.scenarios.length > 0 && (
        <section className="mt-10" data-testid="discovery-section-scenarios">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Scénarios réels</h2>
          <div className="space-y-3">
            {narr.scenarios.map((s, i) => (
              <Card key={i} className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <h4 className="font-serif text-[#92710A] mb-2">{s.context}</h4>
                <p className="text-sm text-[#0F0F10] leading-relaxed mb-2"><span className="text-xs uppercase tracking-widest text-[#4B5563] mr-2">Comportement probable</span>{s.likely_behavior}</p>
                <p className="text-sm text-[#0F0F10] leading-relaxed"><span className="text-xs uppercase tracking-widest text-[#92710A] mr-2">Mouvement recommandé</span>{s.recommended_move}</p>
              </Card>
            ))}
          </div>
        </section>
      )}

      {Array.isArray(narr.signature_recommendations) && narr.signature_recommendations.length > 0 && (
        <section className="mt-10" data-testid="discovery-section-signature-recos">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Recommandations signatures</h2>
          <div className="space-y-3">
            {narr.signature_recommendations.map((r, i) => (
              <div key={i} className="border-l-4 border-[#92710A] bg-[#FBF8F1] rounded-sm p-4">
                <h4 className="font-serif text-lg text-[#92710A] mb-1">{r.title}</h4>
                <p className="text-xs italic text-[#4B5563] mb-2">{r.context}</p>
                <p className="text-sm text-[#0F0F10] leading-relaxed mb-1"><b>Action.</b> {r.action}</p>
                <p className="text-sm text-[#0F0F10] leading-relaxed"><b>Bénéfice.</b> {r.benefit}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {narr.trajectories && (narr.trajectories.if_static || narr.trajectories.if_engaged) && (
        <section className="mt-10" data-testid="discovery-section-trajectories">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Projections à 12 mois</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {narr.trajectories.if_static && (
              <div className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#C0392B] font-semibold uppercase mb-2">Si rien ne change</div>
                <p className="text-sm text-[#0F0F10] leading-relaxed">{narr.trajectories.if_static}</p>
              </div>
            )}
            {narr.trajectories.if_engaged && (
              <div className="border border-[#E5DFD0] bg-[#FBF8F1] rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#2D8659] font-semibold uppercase mb-2">Si vous vous engagez</div>
                <p className="text-sm text-[#0F0F10] leading-relaxed">{narr.trajectories.if_engaged}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {Array.isArray(narr.internal_contradictions) && narr.internal_contradictions.length > 0 && (
        <section className="mt-10" data-testid="discovery-section-contradictions">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Contradictions internes</h2>
          <div className="space-y-3">
            {narr.internal_contradictions.map((c, i) => (
              <Card key={i} className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <p className="text-sm text-[#0F0F10] leading-relaxed font-medium mb-2">{c.tension}</p>
                <p className="text-xs italic text-[#4B5563] mb-1"><b>Origine.</b> {c.origin}</p>
                <p className="text-xs text-[#92710A] leading-relaxed"><b>Levier.</b> {c.unlock}</p>
              </Card>
            ))}
          </div>
        </section>
      )}

      {Array.isArray(narr.cognitive_biases) && narr.cognitive_biases.length > 0 && (
        <section className="mt-10" data-testid="discovery-section-biases">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Biais cognitifs dominants</h2>
          <div className="space-y-3">
            {narr.cognitive_biases.map((b, i) => (
              <div key={i} className="border-l-4 border-[#1E4F8C] bg-white rounded-sm p-4">
                <h4 className="font-serif text-[#1E4F8C] mb-1">{b.name}</h4>
                <p className="text-sm text-[#0F0F10] leading-relaxed mb-1"><b>Manifestation.</b> {b.manifestation}</p>
                <p className="text-sm text-[#0F0F10] leading-relaxed"><b>Antidote.</b> {b.antidote}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {Array.isArray(narr.uncomfortable_truths) && narr.uncomfortable_truths.length > 0 && (
        <section className="mt-10" data-testid="discovery-section-truths">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Vérités inconfortables</h2>
          <ul className="space-y-3">
            {narr.uncomfortable_truths.map((t, i) => (
              <li key={i} className="border-l-4 border-[#C0392B] bg-[#FBF8F1] rounded-sm p-4 text-sm text-[#0F0F10] leading-relaxed">{t}</li>
            ))}
          </ul>
        </section>
      )}

      {Array.isArray(narr.verbal_scripts) && narr.verbal_scripts.length > 0 && (
        <section className="mt-10" data-testid="discovery-section-scripts">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Scripts verbaux</h2>
          <div className="space-y-3">
            {narr.verbal_scripts.map((v, i) => (
              <div key={i} className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#92710A] font-semibold uppercase mb-2">{v.situation}</div>
                <p className="text-sm text-[#0F0F10] leading-relaxed italic">{v.phrase}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {narr.executive_perception && (
        <section className="mt-10" data-testid="discovery-section-perception">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Perception exécutive</h2>
          <div className="space-y-3">
            {narr.executive_perception.how_n_plus_2_sees_you && (
              <div className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#92710A] font-semibold uppercase mb-1">Vu(e) par votre N+2</div>
                <p className="text-sm italic text-[#0F0F10]">{narr.executive_perception.how_n_plus_2_sees_you}</p>
              </div>
            )}
            {narr.executive_perception.how_peers_see_you && (
              <div className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#92710A] font-semibold uppercase mb-1">Vu(e) par vos pairs</div>
                <p className="text-sm italic text-[#0F0F10]">{narr.executive_perception.how_peers_see_you}</p>
              </div>
            )}
            {narr.executive_perception.how_direct_reports_see_you && (
              <div className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#92710A] font-semibold uppercase mb-1">Vu(e) par vos collaborateurs directs</div>
                <p className="text-sm italic text-[#0F0F10]">{narr.executive_perception.how_direct_reports_see_you}</p>
              </div>
            )}
            {narr.executive_perception.perception_gap && (
              <div className="border-l-4 border-[#C0392B] bg-[#FBF8F1] rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#C0392B] font-semibold uppercase mb-1">Écart de perception (votre plafond invisible)</div>
                <p className="text-sm text-[#0F0F10] leading-relaxed">{narr.executive_perception.perception_gap}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {narr.stress_tiers && (
        <section className="mt-10" data-testid="discovery-section-stress">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Réactions sous stress</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { key: 'punctual', label: 'Stress ponctuel', color: '#92710A' },
              { key: 'sustained', label: 'Stress prolongé', color: '#D97706' },
              { key: 'crisis', label: 'Crise', color: '#C0392B' },
            ].map(({ key, label, color }) => {
              const tier = narr.stress_tiers[key];
              if (!tier) return null;
              return (
                <div key={key} className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                  <div className="text-[10px] tracking-widest font-semibold uppercase mb-2" style={{ color }}>{label}</div>
                  {Array.isArray(tier.signals) && tier.signals.length > 0 && (
                    <div className="mb-2">
                      <div className="text-xs font-semibold text-[#4B5563] mb-1">Signaux</div>
                      <ul className="text-sm text-[#0F0F10] space-y-1 list-disc list-inside">
                        {tier.signals.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                  {Array.isArray(tier.levers) && tier.levers.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-[#92710A] mb-1">Leviers</div>
                      <ul className="text-sm text-[#0F0F10] space-y-1 list-disc list-inside">
                        {tier.levers.map((l, i) => <li key={i}>{l}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {narr.trajectory_36_months && (
        <section className="mt-10" data-testid="discovery-section-trajectory-36m">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Trajectoire à 36 mois</h2>
          <div className="space-y-3">
            {[
              { key: 'scenario_ceiling', label: 'Scénario plafond', color: '#C0392B' },
              { key: 'scenario_pivot', label: 'Scénario pivot', color: '#92710A' },
              { key: 'scenario_breakthrough', label: 'Scénario rupture', color: '#2D8659' },
            ].map(({ key, label, color }) => {
              const text = narr.trajectory_36_months[key];
              if (!text) return null;
              return (
                <div key={key} className="border-l-4 bg-white rounded-sm p-4" style={{ borderColor: color }}>
                  <div className="text-[10px] tracking-widest font-semibold uppercase mb-1" style={{ color }}>{label}</div>
                  <p className="text-sm text-[#0F0F10] leading-relaxed">{text}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {narr.signature_move && (narr.signature_move.bet || narr.signature_move.title) && (
        <section className="mt-10" data-testid="discovery-section-signature-move">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">{narr.signature_move.title || 'Votre pari signature'}</h2>
          <div className="border border-[#92710A] bg-gradient-to-br from-[#FBF8F1] to-white rounded-sm p-5">
            {narr.signature_move.bet && (
              <p className="text-base text-[#0F0F10] leading-relaxed mb-3"><b>Le pari.</b> {narr.signature_move.bet}</p>
            )}
            {narr.signature_move.why_now && (
              <p className="text-sm text-[#4B5563] italic mb-3"><b>Pourquoi maintenant.</b> {narr.signature_move.why_now}</p>
            )}
            {narr.signature_move.success_signal && (
              <div className="border-t border-[#E5DFD0] pt-3 mt-3">
                <div className="text-[10px] tracking-widest text-[#2D8659] font-semibold uppercase mb-1">Signal de réussite</div>
                <p className="text-sm text-[#0F0F10] leading-relaxed">{narr.signature_move.success_signal}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {narr.career_x_ray && (
        <section className="mt-10" data-testid="discovery-section-career-xray">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Radiographie carrière</h2>
          <div className="space-y-3">
            {narr.career_x_ray.technical_ceiling && (
              <div className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#1E4F8C] font-semibold uppercase mb-1">Plafond technique</div>
                <p className="text-sm text-[#0F0F10] leading-relaxed">{narr.career_x_ray.technical_ceiling}</p>
              </div>
            )}
            {narr.career_x_ray.political_ceiling && (
              <div className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#92710A] font-semibold uppercase mb-1">Plafond politique</div>
                <p className="text-sm text-[#0F0F10] leading-relaxed">{narr.career_x_ray.political_ceiling}</p>
              </div>
            )}
            {narr.career_x_ray.cultural_ceiling && (
              <div className="border border-[#E5DFD0] bg-white rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#C0392B] font-semibold uppercase mb-1">Plafond culturel</div>
                <p className="text-sm text-[#0F0F10] leading-relaxed">{narr.career_x_ray.cultural_ceiling}</p>
              </div>
            )}
            {Array.isArray(narr.career_x_ray.unlock_levers) && narr.career_x_ray.unlock_levers.length > 0 && (
              <div className="border-l-4 border-[#2D8659] bg-[#FBF8F1] rounded-sm p-4">
                <div className="text-[10px] tracking-widest text-[#2D8659] font-semibold uppercase mb-2">Leviers de déblocage</div>
                <ul className="text-sm text-[#0F0F10] space-y-1 list-disc list-inside">
                  {narr.career_x_ray.unlock_levers.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {(mbtiCode || (mbti && Object.keys(mbti).length > 0)) && (
        <section className="mt-10" data-testid="discovery-section-mbti">
          <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Vos préférences cognitives</h2>
          <p className="text-sm text-[#4B5563] italic mb-3">Quatre dimensions complémentaires — comment vous traitez l'information, décidez et structurez votre action.</p>
          {mbti && Object.keys(mbti).length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {[
                { k: 'EI', left: 'Extraversion (E)', right: 'Introversion (I)' },
                { k: 'SN', left: 'Sensation (S)', right: 'Intuition (N)' },
                { k: 'TF', left: 'Pensée (T)', right: 'Sentiment (F)' },
                { k: 'JP', left: 'Jugement (J)', right: 'Perception (P)' },
              ].map(({ k, left, right }) => {
                const v = mbti[k];
                if (v == null) return null;
                const pct = typeof v === 'number' ? Math.max(0, Math.min(100, v)) : 50;
                return (
                  <div key={k} className="border border-[#E5DFD0] bg-white rounded-sm p-3">
                    <div className="flex justify-between text-xs text-[#4B5563] mb-1">
                      <span>{left}</span><span>{right}</span>
                    </div>
                    <div className="h-2 bg-[#FBF8F1] rounded-full relative">
                      <div className="absolute top-0 h-2 bg-[#92710A] rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {mbtiCode && (
            <p><b>Votre code :</b> <span className="text-[#92710A] font-bold tracking-[3pt]">{mbtiCode}</span></p>
          )}
        </section>
      )}

      <section className="mt-10" data-testid="discovery-section-methodology">
        <h2 className="font-serif text-2xl text-[#0F0F10] border-b border-[#E5DFD0] pb-2 mb-4">Méthodologie</h2>
        <p className="text-[#0F0F10] leading-relaxed mb-3">Ce rapport repose sur un modèle comportemental jungien enrichi, combinant :</p>
        <ul className="list-disc list-inside text-sm text-[#0F0F10] space-y-2">
          <li>Un bloc de 25 questions structurées (échelle Likert 1-7 et choix forcés), dont les pondérations sont déterministes et identiques pour chaque passation.</li>
          <li>Un bloc de 4 questions ouvertes générées dynamiquement par un modèle d'IA exécutif, calibrées sur vos scores intermédiaires pour creuser vos zones d'ambiguïté et vos angles morts.</li>
          <li>Un mapping de 12 archétypes dérivés des combinaisons dominantes ↔ secondaires.</li>
        </ul>
        <p className="text-xs text-[#4B5563] italic mt-3">Le calcul des scores est strictement déterministe — l'IA intervient uniquement pour enrichir la narration. Ce rapport est personnel et confidentiel ; il ne constitue ni un diagnostic clinique, ni une évaluation psychologique.</p>
      </section>

      <p className="text-center text-xs tracking-widest text-[#4B5563] uppercase mt-16 pt-10 border-t border-[#E5DFD0]">
        COACHDIA · PRISM · Rapport personnel
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Helper — redirect via useEffect (évite une redirection au render qui
// casserait les renderings du state machine)
// ─────────────────────────────────────────────────────────────
const Redirecter = ({ to }) => {
  const navigate = useNavigate();
  useEffect(() => { navigate(to, { replace: true }); }, [navigate, to]);
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Page principale — orchestrateur de state
// ─────────────────────────────────────────────────────────────
export default function Discovery() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [session, setSession] = useState(null);
  const [nextQuestion, setNextQuestion] = useState(null);
  const [aiQuestions, setAiQuestions] = useState([]);
  const [aiIndex, setAiIndex] = useState(0);
  const [aiAnswers, setAiAnswers] = useState({}); // {qid: text}
  const [profileSummary, setProfileSummary] = useState(null);
  const [pricing, setPricing] = useState(null);
  const fetchedOnce = useRef(false);

  // Fetch pricing + session at mount
  const fetchPricing = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/discovery/pricing`);
      setPricing(r.data);
    } catch (_) { /* non-bloquant */ }
  }, []);

  const hydrate = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/discovery/session`, { headers: authHeaders() });
      setSession(r.data);
      setNextQuestion(r.data.next_question || null);
      if (r.data.ai_questions?.length) setAiQuestions(r.data.ai_questions);
      if (r.data.status === 'completed_unpaid' || r.data.status === 'paid') {
        const rep = await axios.get(`${API}/discovery/report`, { headers: authHeaders() });
        setProfileSummary(rep.data.profile_summary);
      }
    } catch (e) {
      if (e.response?.status !== 404) {
        console.error('hydrate error', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) { navigate('/login?next=/discovery'); return; }
    if (fetchedOnce.current) return;
    fetchedOnce.current = true;
    Promise.all([fetchPricing(), hydrate()]);
  }, [user, navigate, fetchPricing, hydrate]);

  // Detect return from Stripe checkout
  useEffect(() => {
    const payment = searchParams.get('payment');
    const stripeSid = searchParams.get('session_id');
    if (payment === 'success') {
      (async () => {
        toast.success('Paiement confirmé, activation en cours…');
        // Try sync in case webhook slow
        try { await axios.post(`${API}/discovery/checkout/sync`, {}, { headers: authHeaders() }); } catch (_) {}
        await hydrate();
        setSearchParams({});
      })();
    } else if (payment === 'cancelled') {
      toast.info('Paiement annulé');
      setSearchParams({});
    }
  }, [searchParams, hydrate, setSearchParams]);

  // Track funnel — view_intro (not_started/aucune session) et view_paywall (teaser affiché)
  useEffect(() => {
    const fire = async (event, props) => {
      try {
        await axios.post(`${API}/discovery/track`, { event, ...(props ? { props } : {}) }, { headers: authHeaders() });
      } catch (_) {}
    };
    if (!loading && (!session || session.status === 'none')) {
      fire('view_intro');
    }
    if (session?.status === 'completed_unpaid' && profileSummary) {
      const variant = profileSummary?.experiment?.variant;
      fire('view_paywall', variant ? { variant, experiment: profileSummary.experiment.name } : null);
    }
  }, [loading, session, profileSummary]);

  const startSession = async () => {
    setStarting(true);
    try {
      const r = await axios.post(`${API}/discovery/start`, {}, { headers: authHeaders() });
      setSession(r.data);
      setNextQuestion(r.data.next_question || null);
      if (r.data.status === 'structured_done') {
        await completeStructured();
      }
    } catch (e) {
      // Blocage serveur (409) — un profile est en attente de paiement ou payé
      if (e.response?.status === 409) {
        const detail = e.response?.data?.detail;
        if (typeof detail === 'object' && detail?.redirect_to) {
          navigate(detail.redirect_to);
          toast.info(detail.message || 'Redirection vers votre profil');
          return;
        }
      }
      toast.error(e.response?.data?.detail || 'Impossible de démarrer');
    } finally {
      setStarting(false);
    }
  };

  const submitStructured = async (raw) => {
    if (!nextQuestion) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/discovery/answer`, { question_id: nextQuestion.id, raw }, { headers: authHeaders() });
      setSession(r.data);
      setNextQuestion(r.data.next_question || null);
      if (!r.data.next_question && r.data.status === 'structured_done') {
        await completeStructured();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const completeStructured = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/discovery/complete-structured`, {}, { headers: authHeaders() });
      setSession(r.data);
      setAiQuestions(r.data.ai_questions || []);
      setAiIndex(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération questions IA');
    } finally {
      setSaving(false);
    }
  };

  const submitAi = async (text) => {
    const q = aiQuestions[aiIndex];
    if (!q) return;
    setSaving(true);
    try {
      await axios.post(`${API}/discovery/answer-ai`, { question_id: q.id, text }, { headers: authHeaders() });
      setAiAnswers({ ...aiAnswers, [q.id]: text });
      if (aiIndex + 1 < aiQuestions.length) {
        setAiIndex(aiIndex + 1);
      } else {
        // Finalize — écran de progression dédié affiché par le parent
        setFinalizing(true);
        try {
          const fin = await axios.post(`${API}/discovery/finalize`, {}, { headers: authHeaders() });
          setProfileSummary(fin.data.profile_summary);
          setSession({ ...session, status: fin.data.status, paid: fin.data.paid });
          toast.success('Profil calculé !');
        } finally {
          setFinalizing(false);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const goBackStructured = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/discovery/answer/undo`, {}, { headers: authHeaders() });
      setSession(r.data);
      setNextQuestion(r.data.next_question || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retour impossible');
    } finally {
      setSaving(false);
    }
  };

  const goBackAi = () => {
    if (aiIndex > 0) setAiIndex(aiIndex - 1);
  };

  const initiatePayment = async () => {
    setPaying(true);
    try {
      const origin = window.location.origin;
      const r = await axios.post(`${API}/discovery/checkout`, { origin_url: origin }, { headers: authHeaders() });
      // 🎁 Iter336 — FREE FLOW : si l'admin a configuré une promo à 0 €,
      //   le backend court-circuite Stripe et nous renvoie free_access:true.
      if (r.data?.free_access) {
        toast.success('🎁 Accès offert activé — votre rapport est disponible', { duration: 4000 });
        // Petite tempo pour laisser le toast s'afficher avant la redirection
        setTimeout(() => {
          window.location.href = r.data.redirect_url || '/discovery?free=1';
        }, 600);
        return;
      }
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur paiement');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF8F1]">
        <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
      </div>
    );
  }

  // State machine
  const status = session?.status;
  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="discovery-page">
      {/* Not started yet */}
      {finalizing ? (
        <FinalizingScreen />
      ) : !session || status === 'none' ? (
        <Intro onStart={startSession} starting={starting} />
      ) : status === 'in_progress' && nextQuestion ? (
        <StructuredQuestion
          key={nextQuestion.id}
          question={nextQuestion}
          progress={session.progress}
          onAnswer={submitStructured}
          onBack={goBackStructured}
          canGoBack={(session.progress?.structured_done || 0) > 0}
          saving={saving}
        />
      ) : status === 'structured_done' || (status === 'awaiting_ai' && aiQuestions.length === 0) ? (
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#92710A]" />
          <span className="ml-3 text-[#4B5563]">Génération de vos questions personnalisées…</span>
        </div>
      ) : status === 'awaiting_ai' && aiQuestions.length > 0 ? (
        <OpenQuestion
          key={aiQuestions[aiIndex]?.id}
          question={aiQuestions[aiIndex]}
          index={aiIndex}
          total={aiQuestions.length}
          onSubmit={submitAi}
          onBack={goBackAi}
          canGoBack={aiIndex > 0}
          saving={saving}
        />
      ) : status === 'completed_unpaid' && profileSummary ? (
        <Teaser summary={profileSummary} pricing={pricing} onPay={initiatePayment} paying={paying} />
      ) : status === 'completed_unpaid' ? (
        // Session marquée completed_unpaid mais profile indisponible — redirect
        // vers le dashboard persistant pour ne jamais laisser l'utilisateur en cul-de-sac.
        <Redirecter to="/my-discovery" />
      ) : status === 'paid' && profileSummary ? (
        <FullReport summary={profileSummary} />
      ) : (
        <Intro onStart={startSession} starting={starting} />
      )}
    </div>
  );
}
